import { useMemo, useRef } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import {
    useIslandFallback,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';

/**
 * Null-rendering owner of the professional island's persistent enquiry pill —
 * the professional's primary Get-in-touch action, always one tap away across
 * every tab (the island is mounted in the persistent layout). Shown whenever no
 * scrolled section claims the island.
 *
 * Mobile only: desktop keeps its in-page CTA clusters and no persistent pill.
 * Self-hides when the professional accepts no enquiries (audience gating).
 */
export const ProfessionalIslandFallback = () => {
    const { mobile } = useAppDisplayModeContext();
    const professional = useProfessionalInitialRouteData();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal({
        professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
    });
    // CTA label is configurable per professional in Business Manager (PrimaryCTA).
    const enquiryLabel =
        professional.primaryCTA?.primaryCTALabelTitleCase ?? 'Get in touch';

    // Route the action through a ref so the memoised mode's identity only tracks
    // meaningful state — an unstable mode would loop setFallback → re-render.
    const actionRef = useRef<() => void>(() => {});
    actionRef.current = () =>
        openEnquiryModal({ title: enquiryLabel, ctaLabel: enquiryLabel });

    const mode = useMemo<IslandMode | null>(
        () =>
            mobile && showEnquiryCTA
                ? {
                      kind: 'cta',
                      label: enquiryLabel,
                      onClick: () => actionRef.current(),
                  }
                : null,
        [mobile, showEnquiryCTA, enquiryLabel],
    );
    useIslandFallback(mode);

    return null;
};
